import React, { useEffect, useState } from 'react';
import { CONTACT_EMAIL } from '../../contact/config';
import DocumentPage from '../../components/documentPage/documentPage';
import LegalDocumentBody from '../../components/legalDocument/legalDocument';
import { loadLegalDocuments } from '../../legal/client';
import type { LegalDocument } from '../../legal/interfaces';
import { styles } from './styles';

// novaeon.io/privacy — Novaeon's own privacy policy.
//
// Replaces the hand-written static public/privacy.html, which duplicated the site's theme
// CSS, header, footer and typography in a second place that had to be kept in step by hand.
// The text now lives in Firestore and renders through the same parser and renderer as
// /legal. Design record: plans/NOVAEON_PRIVACY_PAGE_PLAN.md.
//
// NOT host-scoped, unlike /legal. /legal is scoped because PlayerCafe's documents must not
// appear under the Novaeon brand; the reverse does not apply — Novaeon operates
// playercafe.novaeon.io, this policy governs that host too, and the marketing page served at
// the subdomain's root links here from its footer.
const COLLECTION = 'appDatabase';
const DOCUMENT_ID = 'legal';

// One field for now: Novaeon has a privacy policy and no Terms of Use. A second document
// becomes a second field here plus a SandwichContainer per document, the way /legal does it.
const FIELDS = ['privacyPolicy'] as const;

const Privacy: React.FC = () => {

  const [document, setDocument] = useState<LegalDocument | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {

    let cancelled = false;

    (async () => {
      try {
        const [loaded] = await loadLegalDocuments(COLLECTION, DOCUMENT_ID, FIELDS);

        if (!cancelled) { setDocument(loaded); }
      }
      catch (error) {
        // Logged, not surfaced: a visitor cannot act on the difference between "rules not
        // deployed" and "malformed JSON in the console", but whoever is debugging can.
        console.error(error);

        if (!cancelled) { setFailed(true); }
      }
    })();

    return () => { cancelled = true; };
  }, []);

  return (
    <DocumentPage>
      <h1 style={styles.pageTitle}>{document?.title ?? 'Privacy Policy'}</h1>

      {failed ? (
        // Deliberately NOT a bundled copy of the policy — a stale local fallback silently
        // standing in for the live document is the failure this design exists to prevent.
        <p style={styles.paragraph}>
          We could not load this policy right now. This is a problem on our side, not yours.
          Please try again shortly, or email{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} style={styles.link}>{CONTACT_EMAIL}</a> and we
          will send you a copy directly.
        </p>
      ) : !document ? (
        <p style={styles.status}>Loading…</p>
      ) : (
        <LegalDocumentBody document={document} />
      )}
    </DocumentPage>
  );
};

export default Privacy;
